/**
 * usePartyDialog
 *
 * Main state hook for the party dialog.  Owns formData, errors, saving state,
 * duplicate-detection state, draft persistence, and all mutating actions.
 *
 * Implementation lives in:
 *   - ./usePartyDialog-helpers.ts  – pure helpers (no React)
 *   - ./usePartyDialog-save.ts     – save handler + orchestration
 */

import { useState, useCallback, useMemo } from 'react';

import type { Party } from '@/gen/proto';

import {
  EMPTY_FORM_DATA,
  EMPTY_ADDRESS,
  buildInitialFormData,
  validatePartyForm,
  type PartyFormData,
  type ValidationErrors,
} from './party-dialog-helpers';
import {
  computeTitleFromRoles,
  deduplicateEntities,
  buildPartiesMap,
  resolveAddressCountry,
  type CountryIdentifier,
} from './usePartyDialog-helpers';
import { useSaveHandler } from './usePartyDialog-save';
import { useEnabledCurrencies, useBaseCurrency } from '../../../hooks/use-currencies';
import {
  searchByName,
  getPrimaryAddress,
  type Entity,
} from '../../../services/entity-service';
import {
  getCountries,
  getCountryByCode,
  type Country,
} from '../../../services/geolocation-service';
import {
  checkDuplicateEntity,
  getNextPartyCode,
  fetchNextPartyCode,
  listParties,
} from '../../../services/party-service';
import {
  partyTypeToRoles,
  gstRegistrationTypeToString,
  stringToGSTRegistrationType,
} from '../../../utils/billing-helpers';
import { debounce } from '../../../utils/debounce';
import { useToast } from '../../ui';

export type { CountryIdentifier } from './usePartyDialog-helpers';

export interface CheckDuplicatesParams {
  entityName?: string;
  countryCode?: string;
  identifiers: { gstin?: string; pan?: string; cin?: string; tan?: string };
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface UsePartyDialogParams {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (party: Party) => void;
  orgId: string;
  ledgerId: string;
  partyType: import('@/gen/proto').PartyType;
  editingParty?: Party | null;
  existingPartyCodes?: string[];
  countryIdentifiers: CountryIdentifier[];
  storageKey: string;
}

export interface UsePartyDialogReturn {
  formData: PartyFormData;
  setFormData: React.Dispatch<React.SetStateAction<PartyFormData>>;
  isSaving: boolean;
  isCheckingDuplicates: boolean;
  duplicateEntities: Entity[];
  setDuplicateEntities: React.Dispatch<React.SetStateAction<Entity[]>>;
  duplicateEntitiesParties: Map<string, Party[]>;
  setDuplicateEntitiesParties: React.Dispatch<React.SetStateAction<Map<string, Party[]>>>;
  selectedEntity: Entity | null;
  setSelectedEntity: (entity: Entity | null) => void;
  isDuplicateWarningVisible: boolean;
  setShowDuplicateWarning: (v: boolean) => void;
  errors: ValidationErrors;
  setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
  isCurrencyConfirmVisible: boolean;
  setShowCurrencyConfirm: (v: boolean) => void;
  currencyOptions: { value: string; label: string }[];
  baseCurrency: string;
  handleLinkToEntity: (entity: Entity) => Promise<void>;
  handleCreateNew: () => void;
  handleSave: () => Promise<void>;
  handleReset: () => void;
  loadFromLocalStorage: () => PartyFormData | null;
  saveToLocalStorage: (data: PartyFormData) => void;
  clearLocalStorage: () => void;
  fetchPartiesForEntity: (entityId: string) => Promise<Party[]>;
  checkDuplicates: (params: CheckDuplicatesParams) => void;
  titleLabel: string;
  title: string;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePartyDialog(params: UsePartyDialogParams): UsePartyDialogReturn {
  const {
    onClose, onSuccess, orgId, ledgerId, partyType, editingParty,
    existingPartyCodes = [], countryIdentifiers, storageKey,
  } = params;

  const { addToast } = useToast();
  const { currencies: currencyOptions } = useEnabledCurrencies();
  const baseCurrency = useBaseCurrency();

  const state = usePartyDialogState({ partyType, baseCurrency });
  const persistence = usePartyDialogPersistence(storageKey);
  const fetchPartiesForEntity = useFetchPartiesForEntity(orgId, ledgerId);

  const handleLinkToEntity = useLinkToEntityHandler(state);
  const handleCreateNew = useCreateNewHandler(state);
  const checkDuplicates = useCheckDuplicates({ fetchPartiesForEntity, state });

  const validate = useCallback((): boolean => {
    const newErrors = validatePartyForm({ formData: state.formData });
    state.setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [state]);

  const handleSave = useSaveHandler({
    state, clearLocalStorage: persistence.clearLocalStorage, fetchPartiesForEntity,
    orgId, ledgerId, editingParty, countryIdentifiers,
    currencyOptionsLength: currencyOptions.length,
    addToast, validate, onSuccess, onClose,
  });

  const handleReset = useResetHandler({
    state, persistence, partyType, existingPartyCodes, baseCurrency, orgId, ledgerId, addToast,
  });

  const titles = computeTitles(state.formData.roles, partyType, editingParty);

  return buildHookReturn({
    state, persistence, currencyOptions, baseCurrency,
    handleLinkToEntity, handleCreateNew, handleSave, handleReset,
    fetchPartiesForEntity, checkDuplicates, titles,
  });
}

// ---------------------------------------------------------------------------
// Return builder + title helper
// ---------------------------------------------------------------------------

function computeTitles(
  formRoles: string[] | undefined,
  partyType: import('@/gen/proto').PartyType,
  editingParty: Party | null | undefined,
): { titleLabel: string; title: string } {
  const titleRoles = (formRoles ?? []).length > 0
    ? (formRoles ?? [])
    : partyTypeToRoles(partyType);
  const titleLabel = computeTitleFromRoles(titleRoles);
  const title = editingParty ? `Edit ${titleLabel}` : `New ${titleLabel}`;
  return { titleLabel, title };
}

interface BuildHookReturnArgs {
  state: PartyDialogState;
  persistence: PartyDialogPersistence;
  currencyOptions: { value: string; label: string }[];
  baseCurrency: string;
  handleLinkToEntity: (entity: Entity) => Promise<void>;
  handleCreateNew: () => void;
  handleSave: () => Promise<void>;
  handleReset: () => void;
  fetchPartiesForEntity: (id: string) => Promise<Party[]>;
  checkDuplicates: (params: CheckDuplicatesParams) => void;
  titles: { titleLabel: string; title: string };
}

function buildHookReturn(args: BuildHookReturnArgs): UsePartyDialogReturn {
  const {
    state, persistence, currencyOptions, baseCurrency,
    handleLinkToEntity, handleCreateNew, handleSave, handleReset,
    fetchPartiesForEntity, checkDuplicates, titles,
  } = args;
  return {
    formData: state.formData, setFormData: state.setFormData,
    isSaving: state.isSaving, isCheckingDuplicates: state.isCheckingDuplicates,
    duplicateEntities: state.duplicateEntities, setDuplicateEntities: state.setDuplicateEntities,
    duplicateEntitiesParties: state.duplicateEntitiesParties,
    setDuplicateEntitiesParties: state.setDuplicateEntitiesParties,
    selectedEntity: state.selectedEntity, setSelectedEntity: state.setSelectedEntity,
    isDuplicateWarningVisible: state.isDuplicateWarningVisible,
    setShowDuplicateWarning: state.setShowDuplicateWarning,
    errors: state.errors, setErrors: state.setErrors,
    isCurrencyConfirmVisible: state.isCurrencyConfirmVisible,
    setShowCurrencyConfirm: state.setShowCurrencyConfirm,
    currencyOptions, baseCurrency,
    handleLinkToEntity, handleCreateNew, handleSave, handleReset,
    loadFromLocalStorage: persistence.loadFromLocalStorage,
    saveToLocalStorage: persistence.saveToLocalStorage,
    clearLocalStorage: persistence.clearLocalStorage,
    fetchPartiesForEntity, checkDuplicates,
    titleLabel: titles.titleLabel, title: titles.title,
  };
}

// ---------------------------------------------------------------------------
// State + Persistence sub-hooks
// ---------------------------------------------------------------------------

interface PartyDialogState {
  formData: PartyFormData;
  setFormData: React.Dispatch<React.SetStateAction<PartyFormData>>;
  isSaving: boolean;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  isCheckingDuplicates: boolean;
  setCheckingDuplicates: React.Dispatch<React.SetStateAction<boolean>>;
  duplicateEntities: Entity[];
  setDuplicateEntities: React.Dispatch<React.SetStateAction<Entity[]>>;
  duplicateEntitiesParties: Map<string, Party[]>;
  setDuplicateEntitiesParties: React.Dispatch<React.SetStateAction<Map<string, Party[]>>>;
  selectedEntity: Entity | null;
  setSelectedEntity: React.Dispatch<React.SetStateAction<Entity | null>>;
  isDuplicateWarningVisible: boolean;
  setShowDuplicateWarning: React.Dispatch<React.SetStateAction<boolean>>;
  errors: ValidationErrors;
  setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
  isCurrencyConfirmVisible: boolean;
  setShowCurrencyConfirm: React.Dispatch<React.SetStateAction<boolean>>;
}

function usePartyDialogState(args: {
  partyType: import('@/gen/proto').PartyType;
  baseCurrency: string;
}): PartyDialogState {
  const [formData, setFormData] = useState<PartyFormData>(() =>
    buildInitialFormData(args.partyType, args.baseCurrency),
  );
  const [isSaving, setSaving] = useState(false);
  const [isCheckingDuplicates, setCheckingDuplicates] = useState(false);
  const [duplicateEntities, setDuplicateEntities] = useState<Entity[]>([]);
  const [duplicateEntitiesParties, setDuplicateEntitiesParties] = useState<Map<string, Party[]>>(new Map());
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [isDuplicateWarningVisible, setShowDuplicateWarning] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isCurrencyConfirmVisible, setShowCurrencyConfirm] = useState(false);

  return {
    formData, setFormData, isSaving, setSaving,
    isCheckingDuplicates, setCheckingDuplicates,
    duplicateEntities, setDuplicateEntities,
    duplicateEntitiesParties, setDuplicateEntitiesParties,
    selectedEntity, setSelectedEntity,
    isDuplicateWarningVisible, setShowDuplicateWarning,
    errors, setErrors,
    isCurrencyConfirmVisible, setShowCurrencyConfirm,
  };
}

interface PartyDialogPersistence {
  loadFromLocalStorage: () => PartyFormData | null;
  saveToLocalStorage: (data: PartyFormData) => void;
  clearLocalStorage: () => void;
}

function usePartyDialogPersistence(storageKey: string): PartyDialogPersistence {
  const loadFromLocalStorage = useCallback((): PartyFormData | null => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) { return JSON.parse(saved) as PartyFormData; }
    } catch { /* ignore */ }
    return null;
  }, [storageKey]);

  const saveToLocalStorage = useMemo(
    () => debounce((data: PartyFormData) => {
      try { localStorage.setItem(storageKey, JSON.stringify(data)); } catch { /* ignore */ }
    }, 500),
    [storageKey],
  );

  const clearLocalStorage = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
  }, [storageKey]);

  return { loadFromLocalStorage, saveToLocalStorage, clearLocalStorage };
}

// ---------------------------------------------------------------------------
// Entity link / fetch helpers
// ---------------------------------------------------------------------------

function useFetchPartiesForEntity(orgId: string, ledgerId: string): (entityId: string) => Promise<Party[]> {
  return useCallback(async (entityId: string): Promise<Party[]> => {
    try {
      const result = await listParties({ orgId, ledgerId, pageSize: 1000 });
      if (result.data?.parties) {
        return result.data.parties.filter(p => p.entityId === entityId);
      }
      return [];
    } catch { return []; }
  }, [orgId, ledgerId]);
}

function useLinkToEntityHandler(state: PartyDialogState): (entity: Entity) => Promise<void> {
  return useCallback(async (entity: Entity): Promise<void> => {
    state.setSelectedEntity(entity);
    state.setShowDuplicateWarning(false);

    const businessIds = entity.businessIdentifiers ?? [];
    const gstin = businessIds.find(b => b.type === 'gstin')?.value;
    const pan = businessIds.find(b => b.type === 'pan')?.value;
    const cin = businessIds.find(b => b.type === 'cin')?.value;
    const tan = businessIds.find(b => b.type === 'tan')?.value;

    const billingAddr = entity.addresses?.find(
      a => (a.usage?.includes('billing') ?? false) || a.label === 'billing'
    ) ?? getPrimaryAddress(entity);
    const resolvedBilling = await resolveAddressCountry(billingAddr);

    state.setFormData(prev => ({
      ...prev,
      entityId: entity.id,
      entityName: entity.name,
      gstin: gstin ?? '',
      pan: pan ?? '',
      cin: cin ?? '',
      tan: tan ?? '',
      addresses: entity.addresses ?? [],
      billingAddress: resolvedBilling ?? prev.billingAddress,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable setters
  }, []);
}

function useCreateNewHandler(state: PartyDialogState): () => void {
  return useCallback((): void => {
    state.setSelectedEntity(null);
    state.setShowDuplicateWarning(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable setters
  }, []);
}

// ---------------------------------------------------------------------------
// Duplicate detection
// ---------------------------------------------------------------------------

function useCheckDuplicates(args: {
  fetchPartiesForEntity: (id: string) => Promise<Party[]>;
  state: PartyDialogState;
}): (params: CheckDuplicatesParams) => void {
  const { fetchPartiesForEntity, state } = args;

  return useMemo(() =>
    debounce(async (params: CheckDuplicatesParams) => {
      const { entityName, countryCode, identifiers } = params;
      const hasName = Boolean(entityName && entityName.trim().length >= 3);
      const hasIdentifiers = Object.values(identifiers).some(v => v && v.length > 0);

      if (!hasName && !hasIdentifiers) {
        clearDuplicateState(state);
        return;
      }

      state.setCheckingDuplicates(true);
      try {
        const allEntities = await fetchAndDedupEntities({
          hasName, hasIdentifiers, entityName, countryCode, identifiers,
        });

        if (allEntities.length === 0) {
          clearDuplicateState(state);
          return;
        }

        const partiesMap = await buildPartiesMap(allEntities, fetchPartiesForEntity);
        state.setDuplicateEntities(allEntities);
        state.setDuplicateEntitiesParties(partiesMap);

        if (tryAutoLinkSingleMatch({ allEntities, hasIdentifiers, state })) { return; }
        state.setShowDuplicateWarning(true);
      } catch { /* ignore */ } finally {
        state.setCheckingDuplicates(false);
      }
    }, 500),
  // eslint-disable-next-line react-hooks/exhaustive-deps -- state is stable per render
  [fetchPartiesForEntity]);
}

function clearDuplicateState(state: PartyDialogState): void {
  state.setDuplicateEntities([]);
  state.setDuplicateEntitiesParties(new Map());
  state.setShowDuplicateWarning(false);
}

async function fetchAndDedupEntities(args: {
  hasName: boolean;
  hasIdentifiers: boolean;
  entityName?: string;
  countryCode?: string;
  identifiers: { gstin?: string; pan?: string; cin?: string; tan?: string };
}): Promise<Entity[]> {
  const { hasName, hasIdentifiers, entityName, countryCode, identifiers } = args;
  const [nameResults, identifierResults] = await Promise.all([
    hasName && entityName ? searchByName(entityName, countryCode) : Promise.resolve({ data: [] }),
    hasIdentifiers ? checkDuplicateEntity(identifiers) : Promise.resolve({ data: [] }),
  ]);
  return deduplicateEntities(nameResults.data ?? [], identifierResults.data ?? []);
}

function tryAutoLinkSingleMatch(args: {
  allEntities: Entity[];
  hasIdentifiers: boolean;
  state: PartyDialogState;
}): boolean {
  const { allEntities, hasIdentifiers, state } = args;
  if (allEntities.length !== 1 || !hasIdentifiers) { return false; }
  const entity = allEntities[0];
  if (!entity) { return false; }
  state.setSelectedEntity(entity);
  state.setShowDuplicateWarning(false);
  return true;
}

// ---------------------------------------------------------------------------
// Reset
// ---------------------------------------------------------------------------

interface UseResetHandlerArgs {
  state: PartyDialogState;
  persistence: PartyDialogPersistence;
  partyType: import('@/gen/proto').PartyType;
  existingPartyCodes: string[];
  baseCurrency: string;
  orgId: string;
  ledgerId: string;
  addToast: ReturnType<typeof useToast>['addToast'];
}

function useResetHandler(args: UseResetHandlerArgs): () => void {
  const { state, persistence, partyType, existingPartyCodes, baseCurrency, orgId, ledgerId, addToast } = args;

  return useCallback((): void => {
    const roles = partyTypeToRoles(partyType);
    const fallbackCode = getNextPartyCode(roles, existingPartyCodes);
    state.setFormData({
      ...EMPTY_FORM_DATA,
      partyType,
      roles,
      defaultCurrency: baseCurrency,
      code: fallbackCode,
    });
    persistence.clearLocalStorage();
    state.setDuplicateEntities([]);
    state.setDuplicateEntitiesParties(new Map());
    state.setSelectedEntity(null);
    state.setShowDuplicateWarning(false);
    state.setErrors({});
    addToast({ variant: 'success', message: 'Form reset successfully' });
    // Override the prefilled code with the authoritative server value.
    if (orgId && ledgerId) {
      void fetchNextPartyCode(orgId, ledgerId, roles).then(res => {
        if (res.data) {
          const serverCode = res.data;
          state.setFormData(prev => (prev.code === fallbackCode ? { ...prev, code: serverCode } : prev));
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- state/persistence are stable per render
  }, [partyType, existingPartyCodes, baseCurrency, orgId, ledgerId, persistence.clearLocalStorage, addToast]);
}

// Re-export for convenience
export {
  gstRegistrationTypeToString,
  stringToGSTRegistrationType,
  EMPTY_ADDRESS,
  EMPTY_FORM_DATA,
  resolveAddressCountry,
  getCountries,
  getCountryByCode,
};
export type { Country };
